import axios, { AxiosInstance } from 'axios';
import { laeuftAlsApp } from './pwaInstall';

/**
 * Wartungsmodus (V6.73.0, docs/wartungsmodus.md): Phasen, Abmelde-Grund und die Anbindung an alle API-Aufrufe.
 * Die Sperre selbst erzwingt der Server; hier wird nur erkannt, was er meldet, und der Benutzer informiert.
 */

export interface WartungsZustand {
  active: boolean;
  startsAt: string | null;
  durationMin: number | null;
  message: string | null;
  /** Ab hier wird abgemeldet (Super-Admins, installierte App und Helfer-Portal ausgenommen) */
  abmeldenAb?: string | null;
  /** Länge der roten Vollbild-Warnung vor `abmeldenAb` */
  warnungMin?: number;
  /** Spätestens dann schaltet sich die Wartung von selbst aus */
  endetSpaetestens?: string | null;
}

export type WartungsPhase = 'aus' | 'gelb' | 'rot' | 'laeuft';

export interface WartungsStand {
  phase: WartungsPhase;
  /** Sekunden bis zur Abmeldung (null ohne Zeitpunkt) */
  sekunden: number | null;
  abmeldenAbMs: number | null;
}

/**
 * Hinweise aus der Zeit vor V6.73.0 haben keinen `abmeldenAb` — dort gilt `startsAt` und die alte 2-Minuten-Grenze,
 * abgemeldet wird nicht.
 */
export const wartungsStand = (state: WartungsZustand | null, jetzt: number, ohneErreichbarkeit = false): WartungsStand => {
  if (!state?.active) return { phase: 'aus', sekunden: null, abmeldenAbMs: null };
  const zeit = state.abmeldenAb ?? state.startsAt;
  const ms = zeit ? new Date(zeit).getTime() : null;
  const sekunden = ms !== null ? Math.max(0, Math.round((ms - jetzt) / 1000)) : null;
  const warnSek = (state.warnungMin ?? 2) * 60;
  let phase: WartungsPhase = 'gelb';
  if (ohneErreichbarkeit || (sekunden !== null && sekunden === 0)) phase = 'laeuft';
  else if (sekunden !== null && sekunden <= warnSek) phase = 'rot';
  return { phase, sekunden, abmeldenAbMs: ms };
};

const uhrzeit = (d: Date) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
export const uhrzeitText = (iso: string | number | null | undefined): string | null =>
  iso === null || iso === undefined ? null : uhrzeit(new Date(iso));

/** Voraussichtlich wieder möglich ab: Beginn + Dauer, höchstens bis zur 30-Minuten-Grenze. */
export const wiederMoeglichAb = (state: WartungsZustand | null): number | null => {
  if (!state?.active) return null;
  const ab = state.abmeldenAb ?? state.startsAt;
  if (!ab) return state.endetSpaetestens ? new Date(state.endetSpaetestens).getTime() : null;
  const ende = state.endetSpaetestens ? new Date(state.endetSpaetestens).getTime() : null;
  const geplant = state.durationMin ? new Date(ab).getTime() + state.durationMin * 60_000 : null;
  if (geplant !== null && (ende === null || geplant < ende)) return geplant;
  return ende ?? new Date(ab).getTime();
};

/** Wer bei der Abmeldung ausgenommen ist: Super-Admins, Helfende (Portal) und die installierte App — wie im Server. */
export const bleibtAngemeldet = (rolle: string | undefined): boolean =>
  rolle === 'SUPER_ADMIN' || rolle === 'HELFER' || laeuftAlsApp();

// ── Grund der Abmeldung (für die Anmeldeseite) ──

export const ABMELDE_GRUND_KEY = 'wartung_abgemeldet';
export const ABMELDE_EVENT = 'wartung:abmeldung';
/**
 * #658: Der Server hat die Sitzung beendet (401 `SITZUNG_BEENDET`, z. B. „Abmelden erzwingen“) — App und Portal melden sofort ab.
 * `detail.token` ist das Token der abgelehnten Anfrage: Hat ein anderer Tab die Sitzung inzwischen erneuert („Auf allen anderen
 * Geräten abmelden“), steht im Speicher schon ein neues Token, und dann darf niemand abmelden (sonst verlöre auch dieser Tab sie).
 */
export const SITZUNG_ENDE_EVENT = 'sitzung:beendet';
export interface SitzungsEnde { token: string | null }
const meldeSitzungsEnde = (token: string | null): void => {
  window.dispatchEvent(new CustomEvent<SitzungsEnde>(SITZUNG_ENDE_EVENT, { detail: { token } }));
};
const traeger = (wert: unknown): string | null => {
  const m = typeof wert === 'string' ? /^Bearer\s+(.+)$/i.exec(wert.trim()) : null;
  return m ? m[1] : null;
};
/** Abmelden nur, wenn die abgelehnte Anfrage das noch gespeicherte Token trug (ohne erkennbares Token: wie bisher abmelden) */
export const sitzungGiltNochFuer = (e: Event, gespeichert: string | null): boolean => {
  if (!gespeichert) return false;
  const t = (e as CustomEvent<SitzungsEnde>).detail?.token;
  return !t || t === gespeichert;
};

export const merkeWartungsAbmeldung = (): void => {
  try {
    if (!sessionStorage.getItem(ABMELDE_GRUND_KEY)) sessionStorage.setItem(ABMELDE_GRUND_KEY, String(Date.now()));
  } catch { /* privates Fenster: dann eben ohne Grund */ }
};

export const leseWartungsAbmeldung = (): number | null => {
  try {
    const v = Number(sessionStorage.getItem(ABMELDE_GRUND_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch { return null; }
};

export const loescheWartungsAbmeldung = (): void => {
  try { sessionStorage.removeItem(ABMELDE_GRUND_KEY); } catch { /* egal */ }
};

// ── Anbindung an alle API-Aufrufe ──

export const APP_MODUS_HEADER = 'X-App-Modus';

// Nur eigene Aufrufe (relative Adressen) — ein fremder Server bekäme sonst eine unnötige CORS-Vorabfrage
const eigeneAdresse = (url: string | undefined, basis: string | undefined): boolean =>
  !/^https?:\/\//i.test(url ?? '') && !/^https?:\/\//i.test(basis ?? '');

/**
 * Setzt an jeden Aufruf einer Instanz den Header der installierten App und erkennt die Sperre (401 `WARTUNG`).
 * Der Header ist vom Client gesetzt und nur eine Ausnahme von der Abmeldung, kein Sicherheitsmerkmal.
 */
export const ruesteAus = (instanz: AxiosInstance): AxiosInstance => {
  instanz.interceptors.request.use(config => {
    if (laeuftAlsApp() && eigeneAdresse(config.url, config.baseURL)) config.headers.set(APP_MODUS_HEADER, 'installiert');
    return config;
  });
  instanz.interceptors.response.use(undefined, (fehler: unknown) => {
    const antwort = (fehler as { response?: { status?: number; data?: { code?: string } } })?.response;
    if (antwort?.status === 401 && antwort.data?.code === 'WARTUNG') {
      merkeWartungsAbmeldung();
      window.dispatchEvent(new Event(ABMELDE_EVENT));
    } else if (antwort?.status === 401 && antwort.data?.code === 'SITZUNG_BEENDET') {
      const kopf = (fehler as { config?: { headers?: { get?: (n: string) => unknown; Authorization?: unknown } } })?.config?.headers;
      meldeSitzungsEnde(traeger(kopf?.get?.('Authorization') ?? kopf?.Authorization));
    }
    return Promise.reject(fehler);
  });
  return instanz;
};

/** Nur Aufrufe an die eigene Herkunft unter `/api` — auch bei `URL`- und `Request`-Objekten (die sind immer absolut). */
export const eigenerApiWeg = (eingabe: RequestInfo | URL): boolean => {
  try {
    const roh = typeof eingabe === 'string' ? eingabe : eingabe instanceof URL ? eingabe.href : eingabe.url;
    const ziel = new URL(roh, window.location.href);
    return ziel.origin === window.location.origin && ziel.pathname.startsWith('/api/');
  } catch {
    return false;
  }
};

let installiert = false;

/**
 * Einmal beim Start (`index.tsx`, als ERSTER Import): ~40 Dateien legen eigene axios-Instanzen an. Statt jede
 * einzeln umzubauen, rüstet `axios.create` jede neue Instanz aus. `fetch` bekommt den Header für `/api` ebenfalls.
 */
export const installiereWartungsAnbindung = (): void => {
  if (installiert) return;
  installiert = true;
  const urspruenglich = axios.create.bind(axios);
  axios.create = (config?: Parameters<typeof axios.create>[0]) => ruesteAus(urspruenglich(config));
  ruesteAus(axios as unknown as AxiosInstance);

  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    const fetchUrspruenglich = window.fetch.bind(window);
    // Das Helfer-Portal ruft mit `fetch` auf: dort die beendete Sitzung ebenfalls erkennen (Kopie der Antwort lesen)
    const pruefeSitzung = (eingabe: RequestInfo | URL, init: RequestInit | undefined) => (antwort: Response): Response => {
      if (antwort.status === 401) {
        const token = traeger(new Headers(init?.headers ?? (typeof Request !== 'undefined' && eingabe instanceof Request ? eingabe.headers : undefined)).get('Authorization'));
        antwort.clone().json()
          .then((d: { code?: string } | null) => { if (d?.code === 'SITZUNG_BEENDET') meldeSitzungsEnde(token); })
          .catch(() => { /* kein JSON: nichts zu tun */ });
      }
      return antwort;
    };
    window.fetch = (eingabe: RequestInfo | URL, init?: RequestInit) => {
      if (!eigenerApiWeg(eingabe)) return fetchUrspruenglich(eingabe, init);
      if (laeuftAlsApp()) {
        const kopf = new Headers(init?.headers ?? (typeof eingabe === 'object' && 'headers' in eingabe ? eingabe.headers : undefined));
        kopf.set(APP_MODUS_HEADER, 'installiert');
        return fetchUrspruenglich(eingabe, { ...init, headers: kopf }).then(pruefeSitzung(eingabe, init));
      }
      return fetchUrspruenglich(eingabe, init).then(pruefeSitzung(eingabe, init));
    };
  }
};
